import { TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { SPRITESHEET_GAME } from "../Data/Constants";
import * as SFX from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VO from "../Data/VoiceOver";
import { Button } from "./Button";
import { SpineTrack } from "./Character";
import SpotlightFactory from "./SpotlightFactory";
import SpotlightParticles from "./SpotlightParticles";
import { InteruptType, VoiceCategory } from "./VoiceOverManager";

const SPOTLIGHT_ALPHA_ON = 0.35;
const SPOTLIGHT_ALPHA_OFF = 0;
const SPOTLIGHT_FADE_IN_DURATION = 0.1;
const SPOTLIGHT_FADE_OUT_DURATION = 0.25;

export class Luna extends PIXI.Container implements HLF.Input.IFocusable {
    public get name() {
        return "Luna";
    }
    public set endPosition(pPos: PIXI.Point) {
        this._endPosition.copy(pPos);
    }

    public set interactive(pEnabled: boolean) {
        this._inputButton.interactive = pEnabled;
        PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this);
    }

    public get interactive(): boolean {
        return this._inputButton.interactive;
    }
    private readonly _spotlight: PIXI.Sprite;
    private readonly _spotlightGround: PIXI.Sprite;
    private readonly _spotlightParticles: SpotlightParticles;
    private readonly _spine: PIXI.spine.Spine;
    private readonly _spineContainer: PIXI.Container;
    private readonly _inputButton: Button;
    private readonly _endPosition: PIXI.Point = new PIXI.Point();
    private _blinkTimer!: TimelineLite;
    private readonly _messageTokens: any[];
    private _soloCount: number = 0;
    private _fromClick: boolean = false;
    private readonly _unlockableDances: readonly string[] = ["dance_1", "dance_2", "dance_3", "dance_4"];
    private readonly _unlockedDances = ["dance_1"];
    private _currentDance: string = this._unlockedDances[0];
    private instrumentKeyPressCooldown?: number;

    constructor(pInputOffset: PIXI.Point = new PIXI.Point(0, 0)) {
        super();

        this._spotlight = SpotlightFactory.MakeSpotlight();
        this._spotlight.scale.x *= 1.25;
        this._spotlight.position.y += 275;
        this._spotlight.scale.y /= 1.25;
        this._spotlightGround = HLF.PixiUtils.makeSprite("spotlight-soloist-ground", SPRITESHEET_GAME);
        this._spotlightGround.anchor.set(0.5, 1);
        this._spotlightGround.position.y = this._spotlight.position.y + this._spotlight.height;
        this._spotlightGround.width = this._spotlight.width;
        this._spotlightGround.scale.y = 2;
        this._spotlightGround.alpha = 0;
        this._spotlightParticles = new SpotlightParticles([
            {color: 0xFFA3EF, size: "big"},
            {color: 0xFFA3EF, size: "small"},
            {color: 0xA0EAFF, size: "small"},
        ], -10, 10);
        this._spotlightParticles.position.y = 125;
        this.addChild(this._spotlightParticles);
        this.addChild(this._spotlightGround);
        this.addChild(this._spotlight);

        this._spineContainer = new PIXI.Container();
        this._spineContainer.scale.set(0.55);

        this._spine = HLF.PixiUtils.makeSpine("luna");
        this._spine.autoUpdate = false;
        this._spine.position = pInputOffset.clone();
        this._spineContainer.addChild(this._spine);
        // this._spine.alpha = 0;
        this.addChild(this._spineContainer);

        // const kidTestingImage = HLF.PixiUtils.makeSprite("luna", Constants.SPRITESHEET_GAME);
        // this.addChild(kidTestingImage);

        this._inputButton = new Button(this.onInputDown.bind(this));
        // this._inputButton.setVisuals(this._spine);
        this._inputButton.hitArea = new PIXI.Rectangle(
            -this._spineContainer.width * 0.5,
            -this._spineContainer.height * 0.5,
            this._spineContainer.width,
            this._spineContainer.height);
        // this._inputButton.setVisuals(kidTestingImage);
        // this._inputButton.hitArea = new PIXI.Rectangle(
        //     -kidTestingImage.width * 0.4,
        //     -kidTestingImage.height * 0.5,
        //     kidTestingImage.width * 0.8,
        //     kidTestingImage.height);

        this.addChild(this._inputButton);

        this._spine.state.addListener({
            complete: this.onAnimComplete.bind(this),
        });

        this._messageTokens = [
            PubSub.subscribe(Topics.CARMEN, this.onMessagePublished.bind(this)),
            PubSub.subscribe(Topics.START_INTRO, this.startIntro.bind(this)),
        ];
        this.scale.set(0.85);
        this.visible = false;
        this.interactive = false;
        this.on(HLF.Input.Events.POINTER_OVER, this.onPointerOver);
        this.on(HLF.Input.Events.POINTER_OUT, this.onPointerOut);
    }

    public onFocusBegin(): void {
        this.SpotlightOn();
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this.onInputDown();
        }
    }

    public onFocusEnd(): void {
        this.SpotlightOff();
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
    }

    public getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this._spineContainer.width * 0.8, this._spineContainer.height * 0.8);
    }

    public update(pDeltaTime: number): void {
        if (this._spine !== undefined) {
            this._spine.update(pDeltaTime);
        }
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean): void {
        this._spine.destroy(pOptions);
        this._messageTokens.forEach(PubSub.unsubscribe);
        clearTimeout(this.instrumentKeyPressCooldown);
        this._blinkTimer?.kill();
        TweenLite.killTweensOf([this._spotlight, this._spotlightGround]);
        super.destroy(pOptions);
    }

    public SpotlightOn() {
        TweenLite.killTweensOf([this._spotlight, this._spotlightGround], false, {alpha: true});
        TweenLite.to(this._spotlight, SPOTLIGHT_FADE_IN_DURATION, {alpha: SPOTLIGHT_ALPHA_ON});
        TweenLite.to(this._spotlightGround, SPOTLIGHT_FADE_IN_DURATION, {alpha: 1});
    }

    public SpotlightOff() {
        TweenLite.killTweensOf([this._spotlight, this._spotlightGround], false, {alpha: true});
        TweenLite.to(this._spotlight, SPOTLIGHT_FADE_OUT_DURATION, {alpha: SPOTLIGHT_ALPHA_OFF});
        TweenLite.to(this._spotlightGround, SPOTLIGHT_FADE_OUT_DURATION, {alpha: 0});
    }

    public finalDance() {
        this.playAnim("dance_4");
    }

    protected startIntro(): void {
        this.endPosition = new PIXI.Point((-this._spineContainer.width * 3 / 4) + 20, -140);
        this.position.copy(this._endPosition);

        const introTimeline: TimelineLite = new TimelineLite();
        introTimeline.delay(4);
        introTimeline.fromTo(this._spine, 0.2, { pixi: {
            scaleX: 0,
            scaleY: 0,
        }},
        {pixi: {
            scaleX: 1,
            scaleY: 1,
        }}, 0);
        introTimeline.to(this, 0, {visible: true}, 0);

        introTimeline.call(() => {
            // this.switchState(CharacterState.Idle);
            this.playAnim("dance_idle", SpineTrack.Body, true, true);
            this.queueBlink();
            this.registerAsFocusable();
        });
    }

    protected onPointerOver(pEvent: PIXI.interaction.InteractionEvent): void {
        this.SpotlightOn();
    }
    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this.interactive) {
            this.SpotlightOff();
        }
    }
    private registerAsFocusable() {
        PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this);
    }

    private playAnim(pName: string, pTrack: number = SpineTrack.Body, pLoop: boolean = false,
                     pResetSkeleton: boolean = false): number {
        if (this._blinkTimer !== undefined) {
            this._blinkTimer.kill();
        }
        if (pResetSkeleton) {
            this._spine.skeleton.setToSetupPose();
            this._spine.state.tracks = [];
        }
        this._spine.state.setAnimation(pTrack, pName, pLoop);
        this._spine.update(0);
        return this._spine.state.getCurrent(pTrack).animationEnd;
    }

    private queueBlink(): void {
        this._blinkTimer = new TimelineLite();
        this._blinkTimer.call(this.blink, undefined, this, HLF.Random.floatBetween(3, 8));
    }

    private blink(): void {
        this.playAnim("blink", SpineTrack.Eyes);
    }

    private idle(): void {
        this.playAnim("dance_idle", SpineTrack.Body, true, true);
        this.queueBlink();
        this.SpotlightOff();
    }

    private onMessagePublished(pTopic: string, pData?: any): void {
        switch (pTopic) {
            case Topics.SOLO_STARTED:
                ++this._soloCount;
                this.interactive = false;
                this._fromClick = false;
                this._currentDance = HLF.ArrayUtils.random(this._unlockedDances);
                this.playAnim(this._currentDance, SpineTrack.Body, true, true);
                this.SpotlightOn();
                this._spotlightParticles.FadeIn();
                if (this._soloCount === 1) {
                    if (this._currentDance === "dance_2") {
                        // "This happy beat makes Luna move her feet!"
                        Application.instance.voiceOver.play(VO.I4, InteruptType.LESS, VoiceCategory.GAMEPLAY);
                    }
                    else {
                        const vo = HLF.ArrayUtils.random([VO.I1, VO.I3]);
                        Application.instance.voiceOver.play(vo, InteruptType.LESS, VoiceCategory.GAMEPLAY);
                    }
                }
                break;
            case Topics.SOLO_ENDED:
                --this._soloCount;
                if (this._soloCount === 0) {
                    this.interactive = true;
                    this.playAnim("dance_idle", SpineTrack.Body, true, true);
                    this.queueBlink();
                    this.SpotlightOff();
                    this._spotlightParticles.FadeOut();
                    Application.instance.voiceOver.play(VO.U11, InteruptType.LESS, VoiceCategory.GAMEPLAY);
                }
                break;
            case Topics.CULTURE_CHANGE_BEGIN:
                this.interactive = true;
                this._soloCount = 0;
                this.playAnim("dance_idle", SpineTrack.Body, true, true);
                this.queueBlink();
                this.SpotlightOff();
                this._spotlightParticles.FadeOut();
                break;
            case Topics.POWERUP_ONE_UNLOCKED:
            case Topics.POWERUP_TWO_UNLOCKED:
            case Topics.POWERUP_THREE_UNLOCKED:
                const newDance = this._unlockableDances[pData + 1];
                this._unlockedDances.push(newDance);
                this._currentDance = newDance;
                Application.instance.audio.play(SFX.DANCE_UNLOCKED, 1, false, HLF.AudioCategory.SFX);
                if (this._soloCount > 0) {
                    this.playAnim(this._currentDance, SpineTrack.Body, true, false);
                    // "Look at Luna go! It’s a new dance move."
                    Application.instance.voiceOver.play(VO.I5, InteruptType.LESS, VoiceCategory.GAMEPLAY);
                }
                else {
                    // "Luna has a new dance move to show us!""
                    Application.instance.voiceOver.play(VO.I2, InteruptType.LESS, VoiceCategory.GAMEPLAY);
                }
                break;
            case Topics.SAMPLE_PAD_BUTTON_CLICKED:
                this.onInstrumentKeyPressed();
                break;
            default:
                break;
        }
    }

    private onAnimComplete(pTrack: PIXI.spine.core.TrackEntry): void {
        switch (pTrack.animation.name) {
            case "blink":
                this.queueBlink();
                break;
            case "wave":
                this.playAnim("dance_idle", SpineTrack.Body, true, true);
                if (this._fromClick) {
                    this.interactive = true;
                    this.SpotlightOff();
                }
                this._fromClick = false;
                break;
            default:
                break;
        }
    }

    private onInputDown(): void {
        this.interactive = false;
        this._fromClick = true;
        this.playAnim("wave", SpineTrack.Body, false, true);
        this.SpotlightOn();
    }

    private onInstrumentKeysNoLongerBeingPressed() {
        if (this._soloCount === 0) {
            if (this._fromClick) {
                this.interactive = true;
                this.SpotlightOff();
            }
            this.playAnim("dance_idle", SpineTrack.Body, true, true);
        }
        this.instrumentKeyPressCooldown = undefined;
    }

    private onInstrumentKeyPressed() {
        // start dancing and set a timeout
        if (this.instrumentKeyPressCooldown === undefined && this._soloCount === 0) {
            this.instrumentKeyPressCooldown = setTimeout(this.onInstrumentKeysNoLongerBeingPressed.bind(this), 750);
            this._currentDance = HLF.ArrayUtils.random(this._unlockedDances);
            this.playAnim(this._currentDance, SpineTrack.Body, true, false);
        }
        // keep dancing and extend the timeout
        else {
            clearTimeout(this.instrumentKeyPressCooldown);
            this.instrumentKeyPressCooldown = setTimeout(this.onInstrumentKeysNoLongerBeingPressed.bind(this), 750);
        }
    }
}
